import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useToast } from '../../hooks/useToast';
import './VideoChat.css';

// Janus is loaded from CDN and available globally
const Janus = window.Janus;

const VideoChat = ({ sessionId, partner, onEnd }) => {
  const [localStream, setLocalStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const janusRef = useRef();
  const videoroomRef = useRef();
  const { socket } = useSocket();
  const { showToast } = useToast();

  const initializeMedia = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      showToast('Error accessing camera/microphone', 'error');
    }
  }, [showToast]);

  const attachToVideoRoom = useCallback(() => {
    janusRef.current.attach({
      plugin: 'janus.plugin.videoroom',
      success: (pluginHandle) => {
        videoroomRef.current = pluginHandle;
        videoroomRef.current.send({
          message: {
            request: 'join',
            room: sessionId,
            ptype: 'publisher',
            display: 'user'
          }
        });
      },
      error: (error) => {
        console.error('Error attaching to videoroom:', error);
        showToast('Error joining video room', 'error');
      },
      consentDialog: (on) => {
        console.log('Consent dialog:', on);
      },
      webrtcState: (on) => {
        console.log('WebRTC state:', on);
      },
      onmessage: (msg, jsep) => {
        if (jsep) {
          videoroomRef.current.handleRemoteJsep({ jsep });
        }
        const result = msg.result;
        if (result && result.videoroom === 'joined') {
          videoroomRef.current.createOffer({
            tracks: [
              { type: 'audio', capture: true, recv: false },
              { type: 'video', capture: true, recv: false }
            ],
            success: (jsep) => {
              videoroomRef.current.send({
                message: { request: 'publish' },
                jsep
              });
            },
            error: (error) => {
              console.error('Error creating offer:', error);
              showToast('Error starting video stream', 'error');
            }
          });
        }
      },
      onlocalstream: (stream) => {
        if (localVideoRef.current) {
          Janus.attachMediaStream(localVideoRef.current, stream);
        }
      },
      onremotestream: (stream) => {
        if (remoteVideoRef.current) {
          Janus.attachMediaStream(remoteVideoRef.current, stream);
        }
      }
    });
  }, [sessionId, showToast]);

  const initializeJanus = useCallback(() => {
    Janus.init({
      debug: false,
      callback: () => {
        janusRef.current = new Janus({
          server: 'wss://simpelweb.xyz/janus',
          success: () => {
            console.log('Connected to Janus server');
            attachToVideoRoom();
          },
          error: (error) => {
            console.error('Error connecting to Janus:', error);
            showToast('Error connecting to video server', 'error');
          },
          destroyed: () => {
            console.log('Janus connection destroyed');
          }
        });
      }
    });
  }, [showToast, attachToVideoRoom]);

  const cleanup = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (videoroomRef.current) {
      videoroomRef.current.detach();
    }
    if (janusRef.current) {
      janusRef.current.destroy();
    }
  }, [localStream]);

  useEffect(() => {
    const init = async () => {
      await initializeMedia();
      initializeJanus();
    };
    init();
    return cleanup;
  }, [sessionId, initializeMedia, initializeJanus, cleanup]);

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      videoTrack.enabled = !videoTrack.enabled;
      setIsVideoOff(!isVideoOff);
    }
  };

  const handleNext = () => {
    cleanup();
    onEnd();
  };

  const handleReport = () => {
    socket.emit('report-user', { sessionId });
    showToast('User reported', 'info');
  };

  return (
    <div className="video-chat-container">
      <div className="video-section">
        <video 
          ref={remoteVideoRef} 
          className="partner-video" 
          autoPlay 
          playsInline 
        />
        <video 
          ref={localVideoRef} 
          className="host-video" 
          autoPlay 
          playsInline 
          muted 
        />
      </div>
      <div className="chat-section">
        <div className="control-panel">
          <button 
            className={`control-button ${isMuted ? 'active' : ''}`}
            onClick={toggleMute}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>
          <button 
            className={`control-button ${isVideoOff ? 'active' : ''}`}
            onClick={toggleVideo}
          >
            {isVideoOff ? 'Start Video' : 'Stop Video'}
          </button>
          <button 
            className="next-button"
            onClick={handleNext}
          >
            Next (ESC)
          </button>
          <button 
            className="report-button"
            onClick={handleReport}
          >
            Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoChat; 